(() => {
  // Optional Boo Crew hub deep links, revision 2026-10-07.4.

  const initializeDetailsLinks = (root) => {
    let disposed = false;
    let scrollFrame;
    const reveal = (hash) => {
      if (disposed || !hash || hash === '#') return null;
      let id;
      try { id = decodeURIComponent(hash.slice(1)); } catch { return null; }
      const target = document.getElementById(id);
      if (!target || !root.contains(target)) return null;
      let opened = false;
      for (let node = target; node && root.contains(node); node = node.parentElement) {
        if (node.matches?.('details') && !node.open) {
          node.open = true;
          opened = true;
        }
        if (node === root) break;
      }
      return { target, opened };
    };
    const onHashChange = () => {
      const hash = window.location.hash;
      const result = reveal(hash);
      if (!result?.opened) return;
      if (scrollFrame !== undefined) window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = undefined;
        if (!disposed && window.location.hash === hash && root.contains(result.target)) {
          result.target.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
      });
    };
    const onClick = (event) => {
      if (disposed || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link || !root.contains(link) || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      try {
        const url = new URL(link.getAttribute('href'), window.location.href);
        const current = new URL(window.location.href);
        if (url.origin !== current.origin || url.pathname !== current.pathname || url.search !== current.search) return;
        reveal(url.hash);
      } catch {  }
    };
    const unload = (event) => {
      if (!event.target.contains(root)) return;
      disposed = true;
      if (scrollFrame !== undefined) window.cancelAnimationFrame(scrollFrame);
      root.removeEventListener('click', onClick);
      window.removeEventListener('hashchange', onHashChange);
      document.removeEventListener('shopify:section:unload', unload);
    };
    root.addEventListener('click', onClick);
    window.addEventListener('hashchange', onHashChange);
    document.addEventListener('shopify:section:unload', unload);
    onHashChange();
  };

  // Native music transport, revision 2026-10-23. Chimes remain optional Web Audio.
  const soundInstances = new WeakMap();

  const initializeSound = (root) => {
    if (soundInstances.has(root)) return soundInstances.get(root);
    const sound = root.querySelector('[data-howl-sound]');
    const state = sound?.querySelector('[data-howl-sound-state]');
    const status = root.querySelector('[data-howl-sound-status]');
    const porch = root.querySelector('.howl-porch');
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const voices = new Set();
    const mediaListeners = [];
    let desired = false;
    let disposed = false;
    let transport = 0;
    let phase = 'off';
    let music;
    let attempt;
    let startTimer;
    let pulseTimer;
    let lastMediaTime = -1;
    let lastProgressAt = 0;
    let chimeContext;
    let chimeGain;
    const now = () => window.performance?.now?.() ?? Date.now();
    const retryLabel = () => root.dataset.howlMusicRetry || sound?.dataset.unavailableLabel || '';
    const showState = (next, message = '') => {
      phase = next;
      const playing = next === 'playing';
      const starting = next === 'starting';
      root.dataset.sound = playing ? 'on' : 'off';
      root.dataset.howlMusicState = next;
      sound?.setAttribute('aria-pressed', String(playing));
      sound?.setAttribute('aria-busy', String(starting));
      if (state) state.textContent = playing ? sound.dataset.onLabel : starting ? root.dataset.howlMusicStarting || sound.dataset.offLabel : sound.dataset.offLabel;
      if (status) status.textContent = message || (starting ? root.dataset.howlMusicStarting || '' : '');
    };
    const clearStartTimer = () => {
      if (startTimer !== undefined) window.clearTimeout(startTimer);
      startTimer = undefined;
    };
    const clearPulse = () => {
      if (pulseTimer !== undefined) window.clearTimeout(pulseTimer);
      pulseTimer = undefined;
      root.classList.remove('is-music-pulse');
    };
    const syncPulse = () => {
      clearPulse();
      if (disposed || !desired || phase !== 'playing' || document.hidden || !music) return;
      if (music.paused || music.ended) {
        stopMusic(true);
        return;
      }
      if (motion?.matches || root.dataset.motion === 'off') return;
      const time = music.currentTime;
      if (Number.isFinite(time)) {
        const wallTime = now();
        if (Math.abs(time - lastMediaTime) > 0.001) {
          lastMediaTime = time;
          lastProgressAt = wallTime;
        }
        const barTime = ((time - 0.05) % 3 + 3) % 3;
        if (time >= 0.05 && barTime < 0.75 && wallTime - lastProgressAt < 500 && root.dataset.lights !== 'off' && porch?.dataset.lights !== 'off') {
          root.classList.add('is-music-pulse');
        }
      }
      pulseTimer = window.setTimeout(() => {
        pulseTimer = undefined;
        syncPulse();
      }, 100);
    };
    const stopChimes = () => {
      voices.forEach(({ oscillator, gain }) => {
        oscillator.onended = null;
        try { oscillator.stop(); } catch { }
        oscillator.disconnect();
        gain.disconnect();
      });
      voices.clear();
      if (chimeContext && chimeGain) {
        chimeGain.gain.cancelScheduledValues(chimeContext.currentTime);
        chimeGain.gain.setValueAtTime(0, chimeContext.currentTime);
        try { chimeContext.suspend().catch(() => {}); } catch { }
      }
    };
    const prepareChimes = (create) => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!chimeContext && create && AudioContext) {
          chimeContext = new AudioContext();
          chimeGain = chimeContext.createGain();
          chimeGain.connect(chimeContext.destination);
        }
        if (!chimeContext || chimeContext.state === 'closed') return;
        chimeContext.resume().then(() => {
          if (!disposed && desired && !document.hidden) chimeGain.gain.setValueAtTime(0.7, chimeContext.currentTime);
          else stopChimes();
        }).catch(() => {});
      } catch { }
    };
    const playTone = (kind) => {
      if (disposed || !desired || phase !== 'playing' || document.hidden || chimeContext?.state !== 'running') return;
      try {
        const notes = kind === 'ghost' ? [392, 523.25] : kind === 'off' ? [329.63] : kind === 'added' ? [523.25, 659.25] : [440];
        notes.forEach((frequency, index) => {
          const start = chimeContext.currentTime + index * 0.11;
          const oscillator = chimeContext.createOscillator();
          const gain = chimeContext.createGain();
          const voice = { oscillator, gain };
          voices.add(voice);
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(frequency, start);
          gain.gain.setValueAtTime(0, start);
          gain.gain.linearRampToValueAtTime(0.025, start + 0.015);
          gain.gain.exponentialRampToValueAtTime(0.00001, start + 0.15);
          gain.gain.setValueAtTime(0, start + 0.17);
          oscillator.connect(gain);
          gain.connect(chimeGain);
          oscillator.onended = () => {
            if (!voices.delete(voice)) return;
            oscillator.onended = null;
            oscillator.disconnect();
            gain.disconnect();
          };
          oscillator.start(start);
          oscillator.stop(start + 0.18);
        });
      } catch { }
    };
    const stopMusic = (retry = false, preservePreference = false) => {
      transport += 1;
      if (!preservePreference) desired = false;
      attempt = undefined;
      clearStartTimer();
      clearPulse();
      stopChimes();
      showState(preservePreference ? 'paused' : retry ? 'retry' : 'off', retry ? retryLabel() : '');
      music?.pause();
    };
    const fail = (version) => {
      if (disposed || version !== transport) return;
      stopMusic(true);
    };
    const confirmPlayback = () => {
      if (disposed || !desired || document.hidden || !music || music.paused || !attempt?.resolved || !attempt.playing || attempt.version !== transport) return;
      const announce = phase !== 'playing';
      clearStartTimer();
      showState('playing');
      lastMediaTime = -1;
      lastProgressAt = now();
      syncPulse();
      if (announce) playTone('on');
    };
    const onPlaying = () => {
      if (disposed || !desired || document.hidden) {
        music?.pause();
        return;
      }
      if (attempt) attempt.playing = true;
      confirmPlayback();
    };
    const onPause = () => {
      if (!disposed && desired && music?.paused && !document.hidden && phase !== 'paused') stopMusic(true);
    };
    const onWaiting = () => {
      if (disposed || !desired || document.hidden) return;
      if (attempt) attempt.playing = false;
      clearPulse();
      showState('starting');
      if (startTimer === undefined) {
        const version = transport;
        startTimer = window.setTimeout(() => fail(version), 12000);
      }
    };
    const onError = () => { if (desired) fail(transport); };
    const createMusic = () => {
      if (music) return music;
      const source = root.dataset.howlMusicSrc;
      if (!source) throw new Error('MUSIC_SOURCE');
      const url = new URL(source, window.location.href);
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error('MUSIC_SOURCE');
      const player = document.createElement('audio');
      player.hidden = true;
      player.preload = 'none';
      player.loop = true;
      player.volume = 0.8;
      player.setAttribute('data-howl-music-audio', '');
      const listen = (name, handler) => {
        player.addEventListener(name, handler);
        mediaListeners.push([name, handler]);
      };
      listen('playing', onPlaying);
      listen('pause', onPause);
      listen('waiting', onWaiting);
      listen('error', onError);
      listen('ended', onPause);
      listen('timeupdate', syncPulse);
      listen('seeked', syncPulse);
      player.src = url.href;
      root.appendChild(player);
      music = player;
      return player;
    };
    const requestPlay = (deliberate) => {
      if (disposed || !desired || document.hidden) return;
      const version = ++transport;
      clearStartTimer();
      clearPulse();
      showState('starting');
      try {
        const player = createMusic();
        root.querySelectorAll('video').forEach(video => {
          if (!video.paused) video.pause();
        });
        attempt = { version, resolved: false, playing: false };
        startTimer = window.setTimeout(() => fail(version), 12000);
        // Keep play() in the deliberate click's synchronous call stack.
        const playing = player.play();
        prepareChimes(deliberate);
        Promise.resolve(playing).then(() => {
          if (disposed || !desired || version !== transport) {
            if (disposed || !desired || document.hidden) player.pause();
            return;
          }
          attempt.resolved = true;
          confirmPlayback();
        }).catch(() => fail(version));
      } catch { fail(version); }
    };
    const toggle = () => {
      if (disposed) return;
      if (desired) stopMusic();
      else {
        desired = true;
        requestPlay(true);
      }
    };
    const onVisibility = () => {
      if (disposed || !music) return;
      if (document.hidden) stopMusic(false, desired);
      else if (desired) requestPlay(false);
    };
    const onMediaPlay = (event) => {
      if (event.target?.tagName !== 'VIDEO') return;
      if (desired) stopMusic();
      root.querySelectorAll('video').forEach(video => {
        if (video !== event.target && !video.paused) video.pause();
      });
    };
    const onMotion = () => syncPulse();
    const unload = (event) => {
      if (!event.target.contains(root)) return;
      disposed = true;
      stopMusic();
      sound?.removeEventListener('click', toggle);
      root.removeEventListener('play', onMediaPlay, true);
      document.removeEventListener('visibilitychange', onVisibility);
      if (motion?.removeEventListener) motion.removeEventListener('change', onMotion);
      else motion?.removeListener?.(onMotion);
      if (music) {
        mediaListeners.forEach(([name, handler]) => music.removeEventListener(name, handler));
        music.removeAttribute('src');
        music.load();
        music.remove();
      }
      chimeGain?.disconnect();
      try { chimeContext?.close().catch(() => {}); } catch { }
      document.removeEventListener('shopify:section:unload', unload);
      soundInstances.delete(root);
    };
    showState('off');
    if (sound && state) {
      sound.addEventListener('click', toggle);
      sound.hidden = false;
    }
    root.addEventListener('play', onMediaPlay, true);
    document.addEventListener('visibilitychange', onVisibility);
    if (motion?.addEventListener) motion.addEventListener('change', onMotion);
    else motion?.addListener?.(onMotion);
    document.addEventListener('shopify:section:unload', unload);
    soundInstances.set(root, playTone);
    return playTone;
  };

  const ambientInstances = new WeakMap();
  const initializeAmbient = (root) => {
    if (ambientInstances.has(root)) return;
    const porch = root.querySelector('.howl-porch');
    if (!porch) return;
    const button = root.querySelector('[data-howl-motion]');
    const label = button?.querySelector('[data-howl-motion-state]');
    const ghost = root.querySelector('[data-howl-ghost]');
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const effects = {
      flicker: { className: 'is-ambient-flicker', duration: 1200 },
      bats: { className: 'is-ambient-bats', duration: 2800 },
      ghost: { className: 'is-ambient-ghost', duration: 2200 }
    };
    let disposed = false;
    let inView = typeof IntersectionObserver !== 'function';
    let timer;
    let sequence = 0;
    let active;
    let first = true;
    let previous;
    let bag = [];
    let ghostHovered = false;
    let ghostFocused = false;
    let ghostQuietUntil = 0;
    const delay = () => 14000 + Math.floor(Math.random() * 10000);
    const enabled = () => root.dataset.motion !== 'off' && !media?.matches;
    const canRun = () => !disposed && enabled() && !document.hidden && inView && root.isConnected !== false;
    const ghostIdle = () => {
      if (!ghost || ghost.hidden || ghostHovered || ghostFocused || Date.now() < ghostQuietUntil || ghost.getAttribute('aria-expanded') === 'true' || ghost.classList.contains('is-reacting')) return false;
      return !ghost.matches?.(':hover, :focus-within');
    };
    const eligible = (effect) => effect === 'flicker'
      ? root.dataset.lights !== 'off' && porch.dataset.lights !== 'off' && root.dataset.sound !== 'on' && root.dataset.howlMusicState !== 'playing'
      : effect === 'ghost' ? ghostIdle() : true;
    const syncButton = () => {
      const on = enabled();
      button?.setAttribute('aria-pressed', String(on));
      button?.setAttribute('aria-disabled', String(Boolean(media?.matches)));
      if (label) label.textContent = (on ? button.dataset.onLabel : button.dataset.offLabel) || label.textContent;
      if (button) button.hidden = false;
    };
    const cancel = () => {
      sequence += 1;
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
      active = undefined;
      Object.values(effects).forEach(effect => root.classList.remove(effect.className));
    };
    const choose = () => {
      if (first && eligible('bats')) return 'bats';
      const choices = Object.keys(effects).filter(eligible);
      bag = bag.filter(effect => choices.includes(effect));
      if (!bag.length) {
        bag = choices.slice();
        for (let index = bag.length - 1; index > 0; index -= 1) {
          const selected = Math.floor(Math.random() * (index + 1));
          [bag[index], bag[selected]] = [bag[selected], bag[index]];
        }
      }
      const next = bag.findIndex(effect => effect !== previous);
      if (next !== -1) return bag.splice(next, 1)[0];
      const alternative = choices.find(effect => effect !== previous);
      if (alternative) return alternative;
      return bag.shift();
    };
    const queue = (wait = first ? 5000 : delay()) => {
      if (!canRun() || timer !== undefined || active) return;
      const version = ++sequence;
      timer = window.setTimeout(() => {
        timer = undefined;
        if (version !== sequence || !canRun()) return;
        const effect = choose();
        if (!effect) {
          queue(delay());
          return;
        }
        first = false;
        previous = active = effect;
        root.classList.add(effects[effect].className);
        timer = window.setTimeout(() => {
          timer = undefined;
          if (version !== sequence) return;
          root.classList.remove(effects[effect].className);
          active = undefined;
          queue(delay());
        }, effects[effect].duration);
      }, wait);
    };
    const reconcile = () => {
      if (disposed) return;
      if (media?.matches && root.dataset.motion !== 'off') root.dataset.motion = 'off';
      syncButton();
      if (!enabled()) root.classList.remove('is-music-pulse');
      if (!canRun()) {
        cancel();
        return;
      }
      if (active && !eligible(active)) cancel();
      queue();
    };
    const toggle = () => {
      if (disposed || media?.matches) return;
      root.dataset.motion = enabled() ? 'off' : 'on';
      reconcile();
    };
    const onGhostEnter = () => {
      ghostHovered = true;
      if (active === 'ghost') cancel();
      queue();
    };
    const onGhostLeave = () => { ghostHovered = false; };
    const onGhostFocus = () => {
      ghostFocused = true;
      if (active === 'ghost') cancel();
      queue();
    };
    const onGhostBlur = () => { ghostFocused = false; };
    const onGhostAction = () => {
      ghostQuietUntil = Date.now() + 1000;
      if (active === 'ghost') cancel();
      queue();
    };
    const ghostHandlers = [['pointerenter', onGhostEnter], ['pointerleave', onGhostLeave], ['focusin', onGhostFocus], ['focusout', onGhostBlur], ['pointerdown', onGhostAction], ['click', onGhostAction]];
    const mutations = typeof MutationObserver === 'function' ? new MutationObserver(reconcile) : null;
    mutations?.observe(root, { attributes: true, attributeFilter: ['data-motion', 'data-lights', 'data-sound', 'data-howl-music-state'] });
    mutations?.observe(porch, { attributes: true, attributeFilter: ['data-lights'] });
    if (ghost) mutations?.observe(ghost, { attributes: true, attributeFilter: ['aria-expanded', 'hidden', 'class'] });
    const intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      const entry = entries.find(item => item.target === porch);
      if (!entry) return;
      inView = entry.isIntersecting;
      reconcile();
    }, { threshold: 0.05 }) : null;
    const unload = (event) => {
      if (!event.target.contains(root)) return;
      disposed = true;
      cancel();
      root.classList.remove('is-music-pulse');
      button?.removeEventListener('click', toggle);
      document.removeEventListener('visibilitychange', reconcile);
      document.removeEventListener('shopify:section:unload', unload);
      if (media?.removeEventListener) media.removeEventListener('change', reconcile);
      else media?.removeListener?.(reconcile);
      ghostHandlers.forEach(([name, handler]) => ghost?.removeEventListener(name, handler));
      intersection?.disconnect();
      mutations?.disconnect();
      ambientInstances.delete(root);
    };
    ambientInstances.set(root, true);
    if (root.dataset.motion !== 'off') root.dataset.motion = 'on';
    button?.addEventListener('click', toggle);
    document.addEventListener('visibilitychange', reconcile);
    document.addEventListener('shopify:section:unload', unload);
    if (media?.addEventListener) media.addEventListener('change', reconcile);
    else media?.addListener?.(reconcile);
    ghostHandlers.forEach(([name, handler]) => ghost?.addEventListener(name, handler));
    intersection?.observe(porch);
    reconcile();
  };

  const initialize = (scope) => {
    const roots = scope.matches?.('[data-howl-home]')
      ? [scope]
      : scope.querySelectorAll('[data-howl-home]');

    roots.forEach((root) => {
      if (root.dataset.howlInitialized === 'true') return;
      root.dataset.howlInitialized = 'true';
      initializeDetailsLinks(root);
      const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      root.dataset.reducedMotion = String(Boolean(motion?.matches));
      const playTone = initializeSound(root);
      initializeAmbient(root);

      const ghost = root.querySelector('[data-howl-ghost]');
      const greeting = root.querySelector('[data-howl-ghost-greeting]');
      const fallback = root.querySelector('[data-howl-ghost-fallback]');
      let ghostTimer;
      if (ghost && greeting) {
        ghost.addEventListener('click', () => {
          const expanded = ghost.getAttribute('aria-expanded') !== 'true';
          ghost.setAttribute('aria-expanded', String(expanded));
          ghost.setAttribute('aria-label', expanded ? ghost.dataset.closeLabel : ghost.dataset.openLabel);
          greeting.textContent = expanded ? ghost.dataset.greeting : '';
          ghost.classList.toggle('is-saying-boo', expanded);
          window.clearTimeout(ghostTimer);
          ghost.classList.remove('is-reacting');
          if (!motion?.matches) {
            void ghost.offsetWidth;
            ghost.classList.add('is-reacting');
            ghostTimer = window.setTimeout(() => ghost.classList.remove('is-reacting'), 650);
          }
          playTone('ghost');
        });
        ghost.hidden = false;
        if (fallback) fallback.hidden = true;
      }

      const lights = root.querySelector('[data-howl-lights]');
      const porch = lights?.closest('.howl-porch');
      const lightsState = lights?.querySelector('[data-howl-lights-state]');
      let lightTimer;
      if (lights && porch && lightsState) {
        const setLights = (on, interaction = false) => {
          porch.dataset.lights = root.dataset.lights = on ? 'on' : 'off';
          lights.setAttribute('aria-pressed', String(on));
          lightsState.textContent = on ? lights.dataset.onLabel : lights.dataset.offLabel;
          if (!interaction) return;
          window.clearTimeout(lightTimer);
          root.classList.remove('is-lights-changing');
          if (!motion?.matches) {
            void root.offsetWidth;
            root.classList.add('is-lights-changing');
            lightTimer = window.setTimeout(() => root.classList.remove('is-lights-changing'), 450);
          }
          playTone(on ? 'on' : 'off');
        };
        lights.addEventListener('click', () => setLights(porch.dataset.lights !== 'on', true));
        setLights(porch.dataset.lights !== 'off');
        lights.hidden = false;
      }

      const studio = root.querySelector('[data-howl-tag-studio]');
      if (!studio) return;
      const form = studio.querySelector('[data-howl-tag-form]');
      const input = studio.querySelector('[data-howl-dog-name]');
      const svg = studio.querySelector('[data-howl-tag-svg]');
      const tagName = studio.querySelector('[data-howl-tag-name]');
      const status = studio.querySelector('[data-howl-tag-status]');
      if (!form || !input || !svg || !tagName || !status) return;

      const dogName = () => Array.from(input.value.replace(/[\u0000-\u001f\u007f]/g, '').trim()).slice(0, 24).join('');
      const updatePreview = () => {
        const name = dogName() || studio.dataset.defaultName;
        tagName.textContent = name;
        tagName.removeAttribute('textLength');
        tagName.removeAttribute('lengthAdjust');
        tagName.setAttribute('font-size', String(Math.max(28, 50 - Math.max(0, Array.from(name).length - 12) * 2)));
        if (tagName.getComputedTextLength() > 360) {
          tagName.setAttribute('textLength', '360');
          tagName.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        }
        status.textContent = '';
      };

      input.addEventListener('input', updatePreview);
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        const name = dogName();
        if (!name) {
          input.value = '';
          input.reportValidity();
          return;
        }
        updatePreview();
        try {
          const copy = svg.cloneNode(true);
          copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
          copy.setAttribute('width', '1200');
          copy.setAttribute('height', '800');
          copy.removeAttribute('data-howl-tag-svg');
          copy.querySelector('[data-howl-tag-name]')?.removeAttribute('data-howl-tag-name');
          const serialized = new XMLSerializer().serializeToString(copy);
          const file = new Blob(['<?xml version="1.0" encoding="UTF-8"?>\n', serialized], { type: 'image/svg+xml;charset=utf-8' });
          const url = URL.createObjectURL(file);
          const link = document.createElement('a');
          const filename = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'dog';
          link.href = url;
          link.download = `${filename}-boo-crew.svg`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.setTimeout(() => URL.revokeObjectURL(url), 1000);
          status.textContent = studio.dataset.downloadReady;
          playTone('added');
        } catch {
          status.textContent = studio.dataset.downloadError;
        }
      });
      studio.hidden = false;
      updatePreview();
    });
  };

  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
